import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { PeriodResolution } from "./Metrics.types";

dayjs.extend(quarterOfYear);

export class Period {
    readonly base: dayjs.Dayjs;

    constructor(period: string | dayjs.Dayjs) {
        if (dayjs.isDayjs(period)) {
            this.base = period;
            return;
        }
        const quarterlyMatch = period.match(/^(\d{4})-Q([1-4])$/);
        if (quarterlyMatch) {
            this.base = dayjs(quarterlyMatch[1]).quarter(
                parseInt(quarterlyMatch[2], 10),
            );
        } else {
            this.base = dayjs(period);
        }
    }

    static fromShortname(shortname: string): Period {
        const yearOnlyMatch = shortname.match(/^(\d{4})$/);
        if (yearOnlyMatch) {
            return new Period(dayjs(yearOnlyMatch[1]));
        }

        const quarterlyMatch = shortname.match(/^Q([1-4])-(\d{2})$/);
        if (quarterlyMatch) {
            const quarter = parseInt(quarterlyMatch[1], 10);
            const year = `20${quarterlyMatch[2]}`;
            return new Period(dayjs(year).quarter(quarter));
        }

        const monthlyMatch = shortname.match(/^(\d{2})-(\d{2})$/);
        if (monthlyMatch) {
            const month = monthlyMatch[1];
            const year = `20${monthlyMatch[2]}`;
            return new Period(dayjs(`${year}-${month}`));
        }

        throw new Error(`Invalid shortname format: ${shortname}`);
    }

    getShortName(
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): string {
        if (!this.base.isValid()) return "-";
        if (resolution === PeriodResolution.YEARLY) {
            return this.base.format("YYYY");
        }
        if (resolution === PeriodResolution.QUARTERLY) {
            return `Q${this.base.quarter()}-${this.base.format("YY")}`;
        }
        return this.base.format("MM-YY");
    }

    getFullName(
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): string {
        if (!this.base.isValid()) return "-";
        if (resolution === PeriodResolution.YEARLY) {
            return this.base.format("YYYY");
        }
        if (resolution === PeriodResolution.QUARTERLY) {
            return `Q${this.base.quarter()} ${this.base.format("YYYY")}`;
        }
        return this.base.format("MMMM YYYY");
    }

    isBefore(other: string | Period): boolean {
        const otherDate =
            other instanceof Period ? other.base : new Period(other).base;
        if (!this.base.isValid() || !otherDate.isValid()) return false;
        return this.base.isBefore(otherDate);
    }

    isAfter(other: string | Period): boolean {
        const otherDate =
            other instanceof Period ? other.base : new Period(other).base;
        if (!this.base.isValid() || !otherDate.isValid()) return false;
        return this.base.isAfter(otherDate);
    }

    isSameAs(other: string | Period): boolean {
        const otherDate =
            other instanceof Period ? other.base : new Period(other).base;
        return this.base.isSame(otherDate);
    }
}
